import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { NavIcon } from '@/components/navIcons';
import { NAV_ITEMS } from '@/lib/navItems';
import { canOpen } from '@/lib/navAccess';
import { mostVisited, recordVisit } from '@/lib/recentNav';
import { useAuth } from '@/auth/AuthProvider';

/**
 * Bottom navigation for phone and tablet.
 *
 * DESTINATIONS ONLY. The way into the full menu is the button in the header, top left - a bar
 * that mixes "go here" with "open the menu" makes the menu the thing people hit by accident,
 * and it is the one item that is not a destination.
 *
 * QUICK ACCESS (owner, 2026-09-27: "bottom add menu like quick access icons based on recent
 * clicks"): the four are the pages THIS person opens most, scored per user on this device (see
 * recentNav.ts), with the field officer's four - farmers, plots, consent, approvals - filling
 * the slots until there is enough history. A destination the signed-in role cannot open is
 * dropped rather than shown and refused, so the bar never offers a dead end.
 *
 * The four sit in MENU order, not score order: a bar that reshuffles under the thumb every time
 * a page is opened is a bar nobody can learn.
 */
const DEFAULTS = ['farmers', 'plots', 'consent-capture', 'inbox'];
const SLOTS = 4;

export function BottomNav() {
  const { profile, status } = useAuth();
  const location = useLocation();
  const userId = profile?.user.id;
  // Bumped after each visit is recorded, so the bar recomputes from the new scores.
  const [, setVisits] = useState(0);

  const activeKey = location.pathname.replace(/^\//, '').split('/')[0] ?? '';
  useEffect(() => {
    if (!userId || !NAV_ITEMS.some((item) => item.key === activeKey)) return;
    recordVisit(userId, activeKey);
    setVisits((count) => count + 1);
  }, [userId, activeKey]);

  const allowed = NAV_ITEMS
    .filter((item) => status === 'offline' || canOpen(profile, item.key))
    .map((item) => item.key);
  const favourites = userId ? mostVisited(userId, allowed, SLOTS) : [];
  const chosen = [...favourites];
  for (const key of DEFAULTS) {
    if (chosen.length >= SLOTS) break;
    if (allowed.includes(key) && !chosen.includes(key)) chosen.push(key);
  }
  const items = NAV_ITEMS.filter((item) => chosen.includes(item.key));

  return (
    <nav aria-label="Quick navigation" className="bottom-nav">
      {items.map((item) => (
        <NavLink className={({ isActive }) => (isActive ? 'active' : '')} key={item.key} to={`/${item.key}`}>
          <NavIcon name={item.icon} size={20} />
          <span>{shortLabel(item.label)}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/** "Plots and mapping" does not fit under a thumb; the menu keeps the full name. */
function shortLabel(label: string): string {
  const first = label.split(/\s|&/)[0] ?? label;
  return first.length > 10 ? `${first.slice(0, 9)}…` : first;
}
